/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * AthleteProfilePage
 * ------------------
 * /feed/profile — the web twin of the UpVibe Profile screen, with the same
 * sections: identity (photo, name, email, member since), personal details
 * (gender, age, weight, city), lifetime totals, activity mix, and personal
 * records for every activity type.
 *
 * Organizers keep the account form at /dashboard/profile (UserProfilePage).
 */

// useRef was only used by the commented-out Contours; add it back if you restore them.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import APIHelper from '../../../api/APIHelper';
import envConfig from '../../../config/envConfig';
import { Icon, Skeleton, LoadError } from '../../../components/feed/FeedParts';
import { retrieve } from '../../../utils/storage_helper';
import useSignOut from '../../../components/feed/useSignOut';
import AlertDialog from '../../../components/common/AlertDialog';
import { useFeedUser } from '../useFeedData';
import { normalizeActivities, formatDuration, formatDateTime } from '../feedModel';
import { fetchMe, fetchUserProfile, fetchAllActivities } from './profileApi';
import {
  lifetimeStats, activityMix, personalRecords, personalDetails, genderLabel,
  unitOf, memberSince, formatLifetimeDistance, formatMovingTime, formatRecordDistance,
} from './profileModel';
import EditProfileSheet from './EditProfileSheet';
import { ProfileRouteArt, ProfileElevation } from './ProfileRouteArt';
import { ProfileCertificatesCard } from '../certificates/MyCertificatesPage';
import '../../../styles/athleteProfile.css';

const api = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

// Previous header background: topographic contour lines, replaced by the
// sign-in route + elevation artwork (ProfileRouteArt). To restore, uncomment this,
// swap <ProfileRouteArt /> back to <Contours /> below, and drop <ProfileElevation />.
// /** Faint topographic contours behind the hero, redrawn to its size. */
// function Contours() {
//   const ref = useRef(null);
//   useEffect(() => {
//     const cv = ref.current;
//     if (!cv) return undefined;
//     const draw = () => {
//       const r = cv.getBoundingClientRect();
//       if (!r.width) return;
//       const dpr = Math.min(window.devicePixelRatio || 1, 2);
//       cv.width = r.width * dpr;
//       cv.height = r.height * dpr;
//       const c = cv.getContext('2d');
//       if (!c) return;
//       c.setTransform(dpr, 0, 0, dpr, 0, 0);
//       c.clearRect(0, 0, r.width, r.height);
//       [{ x: r.width * 0.86, y: r.height * 0.2, s: 1 }, { x: r.width * 0.62, y: r.height * 1.05, s: 0.8 }].forEach((p, pi) => {
//         for (let i = 1; i <= 22; i += 1) {
//           const rad = i * 16 * p.s;
//           c.beginPath();
//           for (let a = 0; a <= Math.PI * 2 + 0.01; a += 0.05) {
//             const w = 1 + 0.16 * Math.sin(3 * a + i * 0.35 + pi) + 0.07 * Math.sin(7 * a - i * 0.2) + 0.04 * Math.cos(11 * a + pi * 2);
//             const x = p.x + Math.cos(a) * rad * w * 1.25;
//             const y = p.y + Math.sin(a) * rad * w * 0.8;
//             if (a === 0) c.moveTo(x, y); else c.lineTo(x, y);
//           }
//           c.closePath();
//           c.strokeStyle = i % 5 === 0 ? 'rgba(255,106,26,.55)' : 'rgba(255,106,26,.2)';
//           c.lineWidth = i % 5 === 0 ? 1.2 : 0.8;
//           c.stroke();
//         }
//       });
//     };
//     draw();
//     if (typeof ResizeObserver === 'undefined') return undefined;
//     const ro = new ResizeObserver(draw);
//     ro.observe(cv);
//     return () => ro.disconnect();
//   }, []);
//   return <canvas ref={ref} className="ap-topo" aria-hidden="true" />;
// }

function Avatar({ photoUrl, initials, onClick }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [photoUrl]);
  return (
    <button type="button" className="ap-avatar" onClick={onClick} aria-label="Change profile photo">
      <span className="ap-avatar-in">
        {photoUrl && !broken ? <img src={photoUrl} alt="" onError={() => setBroken(true)} /> : initials}
      </span>
      <span className="ap-cam"><Icon name="photo_camera" /></span>
    </button>
  );
}

function ProfileSkeleton() {
  return (
    <div className="ap-grid" aria-busy="true" aria-label="Loading your profile">
      <div className="ap-s12"><Skeleton height={300} radius={28} /></div>
      <div className="ap-s5"><Skeleton height={240} radius={24} /></div>
      <div className="ap-s7"><Skeleton height={240} radius={24} /></div>
      <div className="ap-s12"><Skeleton height={360} radius={24} /></div>
    </div>
  );
}

// Previous 2×2 tile layout for personal details, replaced by the list rows below.
// function DetailTile({ icon, label, children, unset }) {
//   return (
//     <div className="ap-pd-t">
//       <span className="ap-pd-ic"><Icon name={icon} /></span>
//       <span className="ap-eyebrow">{label}</span>
//       <b className={unset ? 'ap-unset' : undefined}>{children}</b>
//     </div>
//   );
// }

/** One personal detail: icon and label on the left, value on the right; "Add" when empty. */
function DetailRow({ icon, label, value, onAdd }) {
  return (
    <div className="ap-dl-row">
      <dt><Icon name={icon} />{label}</dt>
      <dd>
        {value !== null && value !== undefined && value !== ''
          ? value
          : <button type="button" className="ap-dl-add" onClick={onAdd}>Add</button>}
      </dd>
    </div>
  );
}

function RecordCard({ rec, unit }) {
  const a = rec.activity;
  if (!a) {
    return (
      <article className="ap-rec is-empty">
        <div className="ap-rec-h">
          <span className="ap-rec-ic"><Icon name={rec.icon} /></span>
          <div><b>{rec.label}</b><span>No record yet</span></div>
        </div>
        <p className="ap-rec-nudge">Your first {rec.label.toLowerCase()} sets the record.</p>
        <div className="ap-rec-f"><span>Record one in the AthloHive app</span></div>
      </article>
    );
  }
  return (
    <article className={`ap-rec${rec.isNew ? ' is-new' : ''}`}>
      {rec.isNew && <span className="ap-new"><Icon name="emoji_events" fill />NEW PR</span>}
      <div className="ap-rec-h">
        <span className="ap-rec-ic"><Icon name={rec.icon} /></span>
        <div><b>{rec.label}</b><span>{rec.byDistance ? 'Longest distance' : 'Longest session'}</span></div>
      </div>
      {rec.byDistance ? (
        <div className="ap-rec-v">{formatRecordDistance(a.distanceKm, unit)}<small>{unit.toUpperCase()}</small></div>
      ) : (
        <div className="ap-rec-v">{formatDuration(a.durationSec)}</div>
      )}
      <div className="ap-rec-f">
        <span>{rec.byDistance ? formatDuration(a.durationSec) : 'Duration'}</span>
        <span>{formatDateTime(a.startedAt).toUpperCase()}</span>
      </div>
    </article>
  );
}

export default function AthleteProfilePage() {
  const user = useFeedUser();
  const { userId } = user;
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [toast, setToast] = useState(false);
  // Renamed on the way out: `error` and `clearError` are generic enough to be
  // mistaken for the profile queries' own failure state further down.
  const { signOut, busy, error: signOutError, clearError: clearSignOutError } = useSignOut();

  const meQ = useQuery({
    queryKey: ['profile', 'me', userId],
    queryFn: ({ signal }) => fetchMe(api, { signal }),
    staleTime: 5 * 60 * 1000,
  });
  const rowQ = useQuery({
    queryKey: ['profile', 'row', userId],
    queryFn: ({ signal }) => fetchUserProfile(api, { userId, signal }),
    enabled: userId !== null,
    staleTime: 5 * 60 * 1000,
  });
  const actQ = useQuery({
    queryKey: ['profile', 'activities', userId],
    queryFn: ({ signal }) => fetchAllActivities(api, { userId, signal }),
    enabled: userId !== null,
    staleTime: 60 * 1000,
  });

  const activities = useMemo(() => normalizeActivities(actQ.data || []), [actQ.data]);
  const stats = useMemo(() => lifetimeStats(activities), [activities]);
  const movingTime = formatMovingTime(stats.movingSec);
  const mix = useMemo(() => activityMix(activities), [activities]);
  const records = useMemo(() => personalRecords(activities), [activities]);
  const details = useMemo(() => personalDetails(rowQ.data), [rowQ.data]);
  const unit = unitOf(rowQ.data);

  // Name from /user/me; if that failed, the name stored at login — never a
  // partial one, or saving the editor would clear the missing half.
  const name = useMemo(() => {
    const src = meQ.data || retrieve('userData') || {};
    const first = String(src.first_name ?? '').trim();
    const last = String(src.last_name ?? '').trim();
    const full = `${first} ${last}`.trim() || user.name;
    const initials = (first && last ? first[0] + last[0] : full.slice(0, 2)).toUpperCase();
    return { first, last, full, initials };
  }, [meQ.data, user.name]);

  const closeEditor = useCallback(() => setEditing(false), []);
  const onSaved = useCallback(() => {
    setEditing(false);
    queryClient.invalidateQueries({ queryKey: ['profile'] });
    setToast(true);
  }, [queryClient]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(false), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  if (userId === null) return <LoadError what="your profile" />;
  if (rowQ.isLoading || actQ.isLoading || meQ.isLoading) return <div className="ap"><ProfileSkeleton /></div>;
  if (rowQ.isError || actQ.isError) {
    return <LoadError what="your profile" onRetry={() => { rowQ.refetch(); actQ.refetch(); meQ.refetch(); }} />;
  }

  const since = memberSince(rowQ.data?.created_at);
  const email = meQ.data?.email;
  // const hasDetails = details.gender || details.age !== null || details.weightKg !== null || details.city; — only the old tile version used it

  return (
    <div className="ap">
      <div className="ap-grid">
        {/* Identity + lifetime totals */}
        <section className="ap-hero ap-s12" aria-label="Your profile">
          {/* <Contours /> */}
          <ProfileRouteArt />
          <div className="ap-hero-top">
            <Avatar photoUrl={details.photoUrl} initials={name.initials} onClick={() => setEditing(true)} />
            <div className="ap-who">
              {since && <span className="ap-kick"><i />Member since {since}</span>}
              <h2>{name.full}</h2>
              {email && <span className="ap-chip"><Icon name="mail" />{email}</span>}
            </div>
            <div className="ap-hero-act">
              <button type="button" className="ap-btn-light" onClick={() => setEditing(true)}>
                <Icon name="edit_square" />Edit profile
              </button>
            </div>
          </div>
          <ProfileElevation />
          <div className="ap-stats">
            <div className="ap-stat">
              <b>{formatLifetimeDistance(stats.distanceKm, unit)}<small>{unit.toUpperCase()}</small></b>
              <span>Total distance</span>
            </div>
            <div className="ap-stat">
              <b>{stats.activities.toLocaleString('en-US')}<small>ACTS</small></b>
              <span>Total activities</span>
            </div>
            <div className="ap-stat">
              <b>{movingTime.value}<small>{movingTime.unit}</small></b>
              <span>Moving time</span>
            </div>
          </div>
        </section>

        {/* My certificates: the count and the two latest; opens /feed/certificates. */}
        <ProfileCertificatesCard />

        {/* Personal details — list rows. The previous tile markup is kept below, commented. */}
        <section className="ap-card ap-pd-card ap-s5">
          <div className="ap-card-h has-act">
            <div>
              <h3>Personal details</h3>
              <p>Used to estimate your calories</p>
            </div>
            <button type="button" className="ap-card-act" onClick={() => setEditing(true)}>
              <Icon name="edit_square" />Edit
            </button>
          </div>
          <dl className="ap-dl">
            <DetailRow icon="wc" label="Gender" value={genderLabel(details.gender)} onAdd={() => setEditing(true)} />
            <DetailRow icon="calendar_month" label="Age" value={details.age === null ? null : `${details.age} years`} onAdd={() => setEditing(true)} />
            <DetailRow icon="scale" label="Weight" value={details.weightKg === null ? null : `${Number(details.weightKg.toFixed(1))} kg`} onAdd={() => setEditing(true)} />
            <DetailRow icon="height" label="Height" value={details.heightCm === null ? null : `${Number(details.heightCm.toFixed(1))} cm`} onAdd={() => setEditing(true)} />
            <DetailRow icon="call" label="Mobile" value={details.mobile} onAdd={() => setEditing(true)} />
            <DetailRow icon="location_on" label="City" value={details.city} onAdd={() => setEditing(true)} />
          </dl>
        </section>
        {/* Previous tile version:
        
        <section className="ap-card ap-s5">
          <div className="ap-card-h">
            <h3>Personal details</h3>
            <p>Used to estimate your calories</p>
          </div>
          <div className="ap-pd">
            <DetailTile icon="person" label="Gender" unset={!details.gender}>
              {genderLabel(details.gender) || 'Not set'}
            </DetailTile>
            <DetailTile icon="cake" label="Age" unset={details.age === null}>
              {details.age === null ? 'Not set' : <>{details.age}<small>YRS</small></>}
            </DetailTile>
            <DetailTile icon="monitor_weight" label="Weight" unset={details.weightKg === null}>
              {details.weightKg === null ? 'Not set' : <>{Number(details.weightKg.toFixed(1))}<small>KG</small></>}
            </DetailTile>
            <DetailTile icon="location_on" label="City" unset={!details.city}>
              {details.city || 'Not set'}
            </DetailTile>
          </div>
          {!hasDetails && (
            <button type="button" className="ap-link" onClick={() => setEditing(true)}>Add your details</button>
          )}
        </section>

        */}

        {/* Activity mix */}
        <section className="ap-card ap-s7">
          <div className="ap-card-h">
            <h3>Activity mix</h3>
            <p>
              {stats.activities
                ? `${stats.activities.toLocaleString('en-US')} ${stats.activities === 1 ? 'activity' : 'activities'}, all time`
                : 'Nothing recorded yet'}
            </p>
          </div>
          {mix.length ? (
            <div className="ap-mix">
              {mix.map((m) => (
                <div className="ap-mrow" key={m.type}>
                  <span className="ap-mrow-ic"><Icon name={m.icon} /></span>
                  <div className="ap-mrow-mid">
                    <div className="ap-mrow-nm">{m.label}<span>{Math.round((m.count / stats.activities) * 100)}%</span></div>
                    <div className="ap-track"><i style={{ width: `${(m.count / mix[0].count) * 100}%` }} /></div>
                  </div>
                  <span className="ap-mrow-ct">{m.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="ap-none">Record an activity in the AthloHive app and it shows up here.</p>
          )}
        </section>

        {/* Personal records */}
        <section className="ap-card ap-s12">
          <div className="ap-card-h">
            <h3>Personal records</h3>
            <p>Longest distance for each activity. Yoga counts the longest session.</p>
          </div>
          <div className="ap-recs">
            {records.map((rec) => <RecordCard key={rec.type} rec={rec} unit={unit} />)}
          </div>
        </section>

        {/* Sign out.
            The feed's only sign out lives in FeedSidebar, and feed.css hides
            that sidebar below 760px — so on a phone, and on a tablet held in
            portrait, there was no way out of the account at all: the top bar's
            avatar is a link to this page, and the bottom tabs carry none.
            It sits here unconditionally rather than behind a media query. A
            breakpoint would have to agree with the sidebar's own, and any
            disagreement reopens the same hole; showing it twice on a wide
            screen costs nothing. Same hook as the sidebar, so there is one
            sign-out path, and the same dialog reports a failed one. */}
        <section className="ap-s12 ap-signout" aria-label="Account">
          <button
            type="button"
            className="ap-signout-btn"
            onClick={signOut}
            disabled={busy}
          >
            <Icon name="logout" />
            {busy ? 'Signing out…' : 'Sign out'}
          </button>
        </section>
      </div>

      <AlertDialog
        open={!!signOutError}
        title="Logout Failed"
        message={signOutError || ''}
        type="error"
        okLabel="OK"
        onOk={clearSignOutError}
      />

      {editing && (
        <EditProfileSheet
          api={api}
          userId={userId}
          profileId={rowQ.data?.id ?? null}
          name={name}
          details={details}
          onClose={closeEditor}
          onSaved={onSaved}
        />
      )}
      {toast && <div className="ap-toast" role="status"><Icon name="check" />Profile saved</div>}
    </div>
  );
}
